import axiosInstance from "./AxiosInstance"
import { cachedRequest } from "./requestCache"

const API_URL = "/calendar"

export const getCalendarEvents = async ({
  viewStart,
  viewEnd,
  trainerId,
  batchId,
  // Whose calendar, when staff read one student's: the server reads that
  // student's batches itself instead of taking every id in the URL.
  studentEmail,
  studentId,
  category,
  eventType,
  hidePastEvents,
  startDateFilter,
  endDateFilter,
  classroom,
  includeFreeSlots,
  // The day list asks for one page at a time and can search within the day;
  // the month grid sends neither and gets the whole view, which is what it
  // needs to count events per date.
  page,
  limit,
  search,
} = {}) => {
  const params = {}
  if (viewStart) params.viewStart = viewStart
  if (viewEnd) params.viewEnd = viewEnd
  if (trainerId) params.trainerId = trainerId
  if (batchId) params.batchId = batchId
  if (studentEmail) params.studentEmail = studentEmail
  if (studentId) params.studentId = studentId
  if (category) params.category = category
  if (eventType) params.eventType = eventType
  if (typeof hidePastEvents === "boolean") params.hidePastEvents = hidePastEvents
  if (startDateFilter) params.startDateFilter = startDateFilter
  if (endDateFilter) params.endDateFilter = endDateFilter
  if (classroom) params.classroom = classroom
  if (typeof includeFreeSlots === "boolean") params.includeFreeSlots = includeFreeSlots
  if (page) params.page = Number(page)
  if (limit) params.limit = Number(limit)
  if (search && String(search).trim()) params.search = String(search).trim()

  return cachedRequest(`calendar:events:${JSON.stringify(params)}`, 10000, async () => {
    const response = await axiosInstance.get(`${API_URL}/events`, { params })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || null,
      /* Per-day counts, computed by the same code that composes and
         filters the day list — so a cell's number and the list it opens
         can no longer disagree. Present only on unpaged (month) calls.

         The month grid uses getCalendarDayCounts below instead; this is
         kept because it costs nothing (the same composition produces it)
         and it is what the grid falls back to if the counts call fails. */
      countsByDay: response.data.meta?.countsByDay || null,
      message: response.data.message,
    }
  })
}

/**
 * How many entries land on each date in the range, under the same filters
 * /events takes. What the month grid puts on its day badges.
 *
 * Its own endpoint so a month of events does not cross the wire purely to
 * be counted on the client. The server composes the range once and
 * projects it two ways; this asks for the projection the grid needs.
 *
 * Returns { '2026-09-14': 5, '2026-09-15': 2, ... } — dates with nothing
 * on them are absent rather than zero.
 */
export const getCalendarDayCounts = async ({
  viewStart,
  viewEnd,
  trainerId,
  batchId,
  // Whose calendar, when staff read one student's: the server reads that
  // student's batches itself instead of taking every id in the URL.
  studentEmail,
  studentId,
  category,
  eventType,
  hidePastEvents,
  startDateFilter,
  endDateFilter,
  classroom,
  includeFreeSlots,
  search,
} = {}) => {
  const params = {}
  if (viewStart) params.viewStart = viewStart
  if (viewEnd) params.viewEnd = viewEnd
  if (trainerId) params.trainerId = trainerId
  if (batchId) params.batchId = batchId
  if (studentEmail) params.studentEmail = studentEmail
  if (studentId) params.studentId = studentId
  if (category) params.category = category
  if (eventType) params.eventType = eventType
  if (typeof hidePastEvents === "boolean") params.hidePastEvents = hidePastEvents
  if (startDateFilter) params.startDateFilter = startDateFilter
  if (endDateFilter) params.endDateFilter = endDateFilter
  if (classroom) params.classroom = classroom
  if (typeof includeFreeSlots === "boolean") params.includeFreeSlots = includeFreeSlots
  if (search && String(search).trim()) params.search = String(search).trim()

  // No page/limit here, deliberately: the server only produces counts on
  // the unpaged branch, and it strips them anyway.
  return cachedRequest(`calendar:counts:${JSON.stringify(params)}`, 10000, async () => {
    const response = await axiosInstance.get(`${API_URL}/events/counts`, { params })
    return {
      counts: response.data.data || {},
      message: response.data.message,
    }
  })
}
